import React from 'react';

import { isEqual, cloneDeep } from 'lodash';
import { logger } from '$/utils/logger';

export function isEqualDeep<T>(value1: T, value2: T): boolean {
  // Directly compare non-object types using isEqual
  if (
    typeof value1 !== 'object' ||
    value1 === null ||
    typeof value2 !== 'object' ||
    value2 === null
  ) {
    return isEqual(value1, value2);
  }

  // Now, both values are confirmed to be objects (and not null)
  const obj1 = value1 as Record<string, any>;
  const obj2 = value2 as Record<string, any>;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  const filteredKeys1 = keys1.filter((key) => typeof obj1[key] !== 'function');
  const filteredKeys2 = keys2.filter((key) => typeof obj2[key] !== 'function');

  if (filteredKeys1.length !== filteredKeys2.length) {
    return false;
  }

  for (const key of filteredKeys1) {
    if (!filteredKeys2.includes(key) || !isEqualDeep(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

export function usePrevious<T>(value: T): T | undefined {
  const ref = React.useRef<T>();

  React.useEffect(() => {
    // This function runs after the render is committed to the screen
    // we should assign value in effect instead of in the clean up because the clean up run after next render.
    // If we do it in clean up function, then in the next render the value returned will be the second last previous version, not the previous
    // The hasChange runs in the render step so it will compare the current value with the second last previous value, that's not the right
    ref.current = value;
  });

  // Return the value from the last render
  return ref.current;
}

export function useDeepCompareMemo<T>(value: T): T {
  // DD: I don't believe we need to use setState here because the fact we're being called already means
  // the current component is being re-rendered. We just need to store the value and return it.  The key
  // here is that we return the same value as last time if value internals haven't changed.
  const val = React.useRef<T>(value);

  if (!isEqualDeep(value, val.current)) {
    if (val.current === value) {
      // This is the case where we have the same object (hash) but internal values of the hash have changed
      // in which case we need to clone the object to trigger a re-render.  Lets skip and just warn for now...
      val.current = cloneDeep(value);
      val.current = value;
      logger.warn(
        'WARNING - useDeepCompareMemo: value internals have changed, but the hash is the same.',
        { value }
      );
    } else {
      val.current = value;
    }
  }

  return val.current;
}

// IMPORTANT
// This is really the only way to track changes in a particular dependency so we can use
// it in useEffect and only run code if a subset of dependencies has changed

function useHasChangedAndVersion(
  values: unknown[],
  options: {
    deepCompare: boolean;
    skipInitial?: boolean;
  }
): [boolean, number, unknown[] | undefined] {
  const version = React.useRef(0);
  // if deepCompare is true, clone the values to avoid mutating the previous object
  values = options.deepCompare ? cloneDeep(values) : values;

  const prevVals = usePrevious(values);
  if (!prevVals) {
    return [!options?.skipInitial, version.current, prevVals];
  }

  const changed = prevVals.some((prev, i) => {
    // Locale doesn't have toString
    if (typeof prev?.toString === 'undefined') {
      return prev !== values[i];
    }
    let hasChanged = prev?.toString() !== values[i]?.toString();
    if (
      values[i]?.toString().includes('[object Object]') ||
      prev?.toString().includes('[object Object]')
    ) {
      hasChanged = options?.deepCompare ? !isEqualDeep(prev, values[i]) : prev !== values[i];
    }

    return hasChanged;
  });

  if (changed) {
    version.current += 1;
  }

  return [changed, version.current, prevVals];
}

export function useHasChanged(
  values: unknown[],
  options?: {
    skipInitial?: boolean;
  }
): boolean {
  return useHasChangedAndVersion(values, { ...options, deepCompare: false })[0];
}

export function useHasChangedDeep(
  values: unknown[],
  options?: {
    skipInitial?: boolean;
  }
): { hasChanged: boolean; changeVersion: number; prevVals: unknown[] | undefined } {
  const result = useHasChangedAndVersion(values, { ...options, deepCompare: true });
  return { hasChanged: result[0], changeVersion: result[1], prevVals: result[2] };
}
